import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Output,
  TemplateRef,
  input,
  viewChild,
  computed
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { DefaultControlValueAccessor } from '@coin/shared/core/util-helpers';
import { debounce, filter, of, Subject, timer } from 'rxjs';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { InputSize, MatFormFieldClass } from '../../input.types';
import { V2TurbineComponent } from '../turbine/v2-turbine.component';

@Component({
  selector: 'coin-v2-text-input',
  templateUrl: './v2-text-input.component.html',
  styleUrls: ['./v2-text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatInput,
    FormsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatTooltip,
    MatFormField,
    MatLabel,
    MatPrefix,
    V2TurbineComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2TextInputComponent),
      multi: true
    }
  ],
  host: { '[class]': 'getSizeClass()' }
})
export class V2TextInputComponent extends DefaultControlValueAccessor<string> {
  readonly label = input<string>(undefined);
  readonly placeholder = input<string>(undefined);
  readonly icon = input<string>(undefined);
  readonly matFormFieldClass = input<MatFormFieldClass>('customer-grey');
  readonly autoCompleteOptions = input<string[]>(undefined);
  readonly loading = input<boolean>(undefined);
  readonly clearable = input(false);
  readonly prefix = input<string | TemplateRef<unknown>>(undefined);
  readonly suffix = input<string | TemplateRef<unknown>>(undefined);
  readonly size = input<InputSize>('medium');
  /** Time delay (in ms) between manually typing and updating the internal control value. 300 for local load indicators, 500 for global indicators */
  readonly inputDebounce = input<InputDebounceTime>(undefined);
  readonly errorMessage = input<string>(undefined); // otherwise the the control's errors are shown as a tooltip
  readonly showErrorTooltip = input(true);
  readonly direction = input<'ltr' | 'rtl'>('ltr');

  @Output() customClear = new EventEmitter<void>();
  private readonly textInput = viewChild<ElementRef<HTMLInputElement>>('textInput');

  protected setValueDebounced$ = new Subject<string>();
  protected readonly prefixAsTemplate = computed(() => this.prefix() as TemplateRef<unknown>);

  protected getSizeClass(): string {
    return `size-${this.size()}`;
  }

  constructor() {
    super();
    this.setValueDebounced$
      .pipe(
        debounce(() => {
          const inputDebounce = this.inputDebounce();
          return inputDebounce ? timer(inputDebounce) : of(null);
        }),
        filter(value => this.value !== value),
        takeUntilDestroyed()
      )
      .subscribe(value => {
        this.value = value;
      });
  }

  public focus(): void {
    this.textInput()?.nativeElement?.focus();
  }

  public blur(): void {
    this.textInput()?.nativeElement?.blur();
  }

  protected onClear(): void {
    if (!this.clearable()) return;

    if (this.customClear.observed) {
      this.customClear.emit();
    } else if (this.value) {
      this.value = '';
      this.setValueDebounced$.next(this.value);
    }
  }
}

export const INPUT_DEBOUNCE_TIMES = [0, 300, 500, 600] as const;
export type InputDebounceTime = (typeof INPUT_DEBOUNCE_TIMES)[number];
